/**
 * The browser's half of counting.
 *
 * One instance for the application, because that is what it is: a session has one funnel, and two
 * of these would be two half-funnels that neither agrees with. Created lazily so that a browser
 * which never gets past the consent question never builds one.
 *
 * **Consent is read every time, not captured once.** Somebody who declines mid-session stops being
 * counted immediately rather than at the next reload — which is the only reading of "opt-in" that
 * a person would recognise as one.
 *
 * **The flush on the way out is the one that matters.** A session whose events are still in memory
 * when the tab closes is a session that never happened as far as the funnel is concerned, and the
 * only reliable moment to catch it is `visibilitychange` — `beforeunload` does not fire on mobile
 * Safari, which is where half of the sessions this product will have are.
 *
 * **`keepalive` rather than `sendBeacon`.** The two do the same job, and `fetch` is the one that
 * takes the same headers and the same credentials policy as everything else here — one way of
 * talking to the product rather than two.
 */
import {
  createTelemetry,
  readConsent,
  type Consent,
  type Telemetry,
  type TelemetryEvent,
} from '@orbit-mind/telemetry';

/**
 * Where the answer lives.
 *
 * `localStorage` rather than the journal's IndexedDB: this has to be readable *synchronously*, on
 * the first render, before anything is counted — and it is one string that is not a person's work.
 */
const CONSENT_KEY = 'orbit-mind.analytics-consent';

export function currentConsent(): Consent {
  try {
    return readConsent(window.localStorage.getItem(CONSENT_KEY));
  } catch {
    // A browser with storage disabled — private mode, an enterprise policy. Not having asked is
    // the honest reading, and it means nothing is counted.
    return 'unknown';
  }
}

export function recordConsent(consent: Consent): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, consent);
  } catch {
    // Nothing to do: this session is simply never counted, which is the safe direction.
  }
}

async function post(events: readonly TelemetryEvent[]): Promise<void> {
  await fetch('/beta/events', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    // The point of the whole file: a batch sent as the tab closes still goes.
    keepalive: true,
    body: JSON.stringify({ events }),
  });
}

let instance: Telemetry | null = null;

function telemetry(): Telemetry {
  instance ??= createTelemetry({
    transport: { send: post },
    environment: {
      consent: currentConsent,
      schedule: (task, delayMs) => {
        const handle = setTimeout(task, delayMs);
        return () => {
          clearTimeout(handle);
        };
      },
    },
  });
  return instance;
}

/** Counts one thing. Safe to call anywhere: it does nothing at all without consent. */
export function count(event: TelemetryEvent): void {
  telemetry().count(event);
}

/**
 * Sends whatever is waiting, now.
 *
 * Called when a session ends, and by anything else that knows the moment matters more than the
 * timer does — a test, or a screen that is about to navigate away from the product entirely.
 * Resolves either way: a failed flush is never the caller's problem.
 */
export function flushNow(): Promise<void> {
  return telemetry().flush();
}

/**
 * Starts listening for the moment a session ends.
 *
 * Returns the unsubscribe, so a test can take it back. Called once, from the application's entry
 * point, because a listener per component would be a flush per component.
 */
export function watchForTheEnd(): () => void {
  const onHidden = (): void => {
    if (window.document.visibilityState === 'hidden') void flushNow();
  };

  window.document.addEventListener('visibilitychange', onHidden);
  return () => {
    window.document.removeEventListener('visibilitychange', onHidden);
  };
}
